import type { GarageView } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { useEffect } from 'react';
import { DirectoryTagsField } from '../../../components/DirectoryTagsField';
import type { DirectoryTagView } from '../../directory-tags/directory-tags.api';
import { GarageZoneField } from './GarageZoneField';

interface GarageFormDrawerProps {
  open: boolean;
  /** Present to edit that garage; absent to create a new one. */
  editing?: GarageView;
  catalogue: DirectoryTagView[];
  error: string | null;
  submitting: boolean;
  onSubmit: (values: Record<string, unknown>) => Promise<boolean>;
  onClose: () => void;
}

function toFormValues(editing: GarageView | undefined): Record<string, unknown> {
  if (!editing) return { name: '', zoneCode: undefined, phone: '', email: '', serviceTypes: [], certifications: '' };
  return {
    name: editing.name,
    zoneCode: editing.zoneCode ?? undefined,
    phone: editing.phone,
    email: editing.email,
    serviceTypes: editing.serviceTypes,
    certifications: editing.certifications,
  };
}

interface GarageFieldsProps {
  editing?: GarageView;
  catalogue: DirectoryTagView[];
  error: string | null;
  submitting: boolean;
}

/** Name, zone, phone, email, service types, certifications and the submit button. */
function GarageFields({ editing, catalogue, error, submitting }: GarageFieldsProps) {
  return (
    <div className="p-5">
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input autoFocus />
      </Form.Item>
      <Form.Item
        name="zoneCode"
        label="Zone"
        rules={[{ required: true, message: 'Choose a zone' }]}
        extra="Where trucks are driven to. Any kind of zone will do."
      >
        <GarageZoneField />
      </Form.Item>
      <Form.Item name="phone" label="Phone">
        <Input inputMode="tel" placeholder="0712 345 678" />
      </Form.Item>
      <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email address' }]}>
        <Input />
      </Form.Item>
      <DirectoryTagsField
        name="serviceTypes"
        label="Service types"
        extra="What this garage can work on — tyres, welding, engine overhaul. Type a value and press enter."
        catalogue={catalogue}
        carried={editing?.serviceTypes ?? []}
      />
      <Form.Item name="certifications" label="Certifications">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item className="mb-0 pt-2">
        <Button type="primary" htmlType="submit" loading={submitting}>
          {editing ? 'Save' : 'Create garage'}
        </Button>
      </Form.Item>
    </div>
  );
}

/** Creating or editing a garage (gitaumoses4/chukua#173): a short form, its place chosen from the zones rather than pinned on a map (#421). */
export function GarageFormDrawer({ open, editing, catalogue, error, submitting, onSubmit, onClose }: GarageFormDrawerProps) {
  const [form] = Form.useForm<Record<string, unknown>>();

  useEffect(() => {
    if (open) form.setFieldsValue(toFormValues(editing));
  }, [open, editing, form]);

  async function submit(values: Record<string, unknown>) {
    if (await onSubmit(values)) onClose();
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : 'New garage'}
      placement="right"
      size={480}
      destroyOnHidden
      classNames={{ body: 'p-0!' }}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <GarageFields editing={editing} catalogue={catalogue} error={error} submitting={submitting} />
      </Form>
    </Drawer>
  );
}
